import {
  APICredentialsListResponse,
  APICredentialsResponse,
  CreateAPICredentials
} from '@cnds-types/apicredentials';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AxiosRequestConfig } from 'axios';

import { createAPIKey, deleteAPIKey, getAPIKeys } from 'api/coreService';

/**
 * A custom hook to get all apiKeys related things from the CoreService.
 *
 * @returns DefinedUseQueryResult<APICredentialsListResponse, Error>
 */
export const useAPIKeysQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['apiKeys', config ? JSON.stringify(config.params) : ''],
    queryFn: async () => {
      const apiKeys = await getAPIKeys(config);
      return apiKeys;
    }
  });

  return query;
};

/**
 * A custom hook to create and delete apiKeys.
 *
 * @returns DefinedUseMutationResult<APICredentialsListResponse, Error, Omit<CreateAPICredentials, 'companyId' | 'account'>>
 */
export const useAPIKeysMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();

  const queryKey = ['apiKeys'];
  const creation = useMutation<
    APICredentialsResponse,
    Error,
    Pick<CreateAPICredentials, 'name' | 'description'>
  >({
    mutationFn: async args => {
      return await createAPIKey(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  const deletion = useMutation<APICredentialsListResponse, Error, string>({
    mutationFn: async args => {
      return await deleteAPIKey(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  return { creation, deletion } as const;
};
